package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.api.dto.QuantResultDto
import org.zq.astrock_ai.quant.QuantBar
import org.zq.astrock_ai.quant.QuantEngine
import org.zq.astrock_ai.quant.positionContextOf
import org.zq.astrock_ai.quant.toDto
import org.zq.astrock_ai.ui.research.gushi.model.Candle

fun Candle.toQuantBar(): QuantBar = QuantBar(
    date = date,
    open = open,
    high = high,
    low = low,
    close = close,
    volume = volume,
)

fun List<Candle>.toQuantBars(): List<QuantBar> = mapNotNull { c ->
    if (c.close <= 0) null else c.toQuantBar()
}

fun List<Candle>.localQuantPlan(
    strategyId: String = "dual_ma",
    lookbackDays: Int = 90,
    costPrice: Double? = null,
    quantity: Int? = null,
    params: Map<String, String> = emptyMap(),
): QuantResultDto? {
    val bars = toQuantBars()
    if (bars.size < 20) return null
    val pos = positionContextOf(costPrice, quantity, bars.lastOrNull()?.close)
    return QuantEngine.buildPlan(
        bars = bars,
        strategyId = strategyId,
        params = params,
        lookbackDays = lookbackDays,
        position = pos,
    ).toDto()
}

/** UI 快捷参数档：id → 传给引擎的 params */
fun strategyParamPreset(strategyId: String, presetId: String): Map<String, String> = when (strategyId) {
    "dual_ma" -> when (presetId) {
        "fast" -> mapOf("fast" to "5", "slow" to "20")
        "slow" -> mapOf("fast" to "10", "slow" to "30")
        else -> emptyMap()
    }
    "rsi_reversal" -> when (presetId) {
        "loose" -> mapOf("period" to "14", "oversold" to "25", "overbought" to "75")
        else -> mapOf("period" to "14", "oversold" to "30", "overbought" to "70")
    }
    "breakout" -> when (presetId) {
        "wide" -> mapOf("lookback" to "30")
        else -> mapOf("lookback" to "20")
    }
    else -> emptyMap()
}

fun strategyDefaultParams(strategyId: String): Map<String, String> =
    QuantEngine.listStrategies()
        .firstOrNull { it.id == strategyId }
        ?.defaultParams
        .orEmpty()

fun resolveStrategyParams(
    strategyId: String,
    presetId: String,
    customParams: Map<String, String>,
): Map<String, String> {
    val defaults = strategyDefaultParams(strategyId)
    if (defaults.isEmpty()) return emptyMap()
    val fromPreset = strategyParamPreset(strategyId, presetId)
    val base = if (fromPreset.isNotEmpty()) defaults + fromPreset else defaults
    return base + customParams.filterValues { it.isNotBlank() }
}

fun detectQuantStrategyId(message: String): String = when {
    message.contains("共识") || message.contains("组合") || message.contains("共振") -> "consensus"
    message.contains("RSI", ignoreCase = true) ||
        message.contains("超买") ||
        message.contains("超卖") -> "rsi_reversal"
    message.contains("突破") -> "breakout"
    else -> "dual_ma"
}

fun wantsQuantQuestion(message: String): Boolean {
    val keys = listOf(
        "量化", "回测", "策略", "双均线", "突破", "信号", "胜率", "回撤",
        "RSI", "超买", "超卖", "共识", "组合", "共振",
    )
    return keys.any { message.contains(it, ignoreCase = true) }
}

fun wantsScanQuestion(message: String): Boolean {
    val keys = listOf("扫描", "自选扫描", "批量", "全自选", "扫一遍", "共识扫描")
    return keys.any { message.contains(it) }
}
